/*
 * Copyright Evollis 2021
 * All Rights Reserved.
 *
 * All information contained herein is, and remains the property of Evollis.
 * The intellectual and technical concepts contained herein are
 * proprietary to Evollis and are protected by trade secret and copyright law.
 * Dissemination of this information or reproduction of this material
 * is STRICTLY FORBIDDEN unless prior written permission is obtained
 * from Evollis.
 */
import Footer from "../../components/main/Footer"
import Basket from "../../components/organisms/Basket"
import { Header } from "../../components/organisms/Header"
import { Topbar } from "../../components/organisms/Topbar"
import { STEPS } from "../../constants"
import { getCountryFromLanguage, initPdiComponent } from "../../core/service/BusinessLogicService"
import { getSessionVoyageurLang, resolveOptinsByLanguage } from "../../core/voyageurLanguage"
import useGetOptinsByTenantAndCountry from "../../hooks/useGetOptinsByTenantAndCountry"
import { finalFormValuesToTunnelData, tunnelDataToFinalForm } from "../../mappers/tunnelDataMapper"
import TradeInModals from "../TradeIn/TradeInModals"
import { useTradeInFlow } from "../TradeIn/useTradeInFlow"
import IdentificationPageForm from "./components/IdentificationPageForm"
import decorator from "./decorator/decorator.js"
import "./styles.css"
import validatorBE from "./validator/validatorByCountry/BEValidator.js"
import validatorFR from "./validator/validatorByCountry/FRValidator.js"
import validatorIT from "./validator/validatorByCountry/ITValidator.js"
import validatorNL from "./validator/validatorByCountry/NLValidator.js"
import validatorPT from "./validator/validatorByCountry/PTValidator.js"
import { setIn } from "final-form"
import createDecorator from "final-form-focus"
import React, { useEffect, useMemo } from "react"
import { Col, Container, Row } from "react-bootstrap"
import * as FinalForm from "react-final-form"

const focusOnErrors = createDecorator()

const IdentificationPage = (props) => {
  const { tunnelData, onSetStep, history } = props
  const { hideIdentificationPageOptins } = tunnelData
  const country = getCountryFromLanguage(tunnelData?.data?.order?.orderLanguageTag)

  const enabled = hideIdentificationPageOptins === null || !hideIdentificationPageOptins
  const hasTunnelDataOptins = enabled && tunnelData?.optins?.length > 0
  const { optins: fetchedOptins, isLoadingOptins: isLoadingFetchedOptins } = useGetOptinsByTenantAndCountry(tunnelData, enabled && !hasTunnelDataOptins, STEPS.IDENTIFICATION)
  const rawOptins = hasTunnelDataOptins ? tunnelData.optins : fetchedOptins
  const isLoadingOptins = hasTunnelDataOptins ? false : isLoadingFetchedOptins

  const selectedLanguage = getSessionVoyageurLang();

  const optins = useMemo(
    () => resolveOptinsByLanguage(rawOptins, selectedLanguage, tunnelData.optins),
    [rawOptins, selectedLanguage, tunnelData.optins]
  )

  const getOptinAnswers = (values) => {
    if (!optins || optins.length === 0) return []

    return optins?.map((optin) => {
      return {
        code: optin.code,
        checked: values[optin.code] === true || values[optin.code] === "true"
      }
    })
  }

  useEffect(() => {
    initPdiComponent("IdentificationPage")
  }, [])

  const {
    submit,
    showTradeInModal,
    showIframeModal,
    showLoaderModal,
    loaderStatus,
    handleConfirm,
    handleCancel,
    handleCloseIframe,
    handleLoaderContinue
  } = useTradeInFlow({ tunnelData, onNextStep: onSetStep, country, getOptinAnswers, finalFormValuesToTunnelData })

  const validateFields = async (values) => {
    let validator = validatorIT
    if (country === "BE") {
      validator = validatorBE
    } else if (country === "FR") {
      validator = validatorFR
    } else if (country === "PT") {
      validator = validatorPT
    } else if (country === "NL") {
      validator = validatorNL
    }

    let hasError = false
    let errors = await validator({ ...values, countryLanguage: country }).catch((invalideFields) => {
      hasError = true
      return invalideFields.inner
        ?.filter((field) => field.errors.length > 0)
        .reduce((errors, errorField) => setIn(errors, errorField.path, errorField.message), {})
    })

    return hasError ? filterErrorsToMatchClientPageConfig(errors) : null
  }
  const filterErrorsToMatchClientPageConfig = (errors = {}) => {
    const filtredErrors = {}
    for (let [bloc, fields] of Object.entries(errors)) {
      if (tunnelData.clientPageConfiguration[bloc]) {
        const filtredFields = {}
        for (let [field, errorMessage] of Object.entries(fields)) {
          if (tunnelData.clientPageConfiguration[bloc].includes(field)) {
            filtredFields[field] = errorMessage
          }
        }
        filtredErrors[bloc] = filtredFields
      }
    }
    return filtredErrors
  }

  const customer = tunnelData.data.order?.customer
  const person = tunnelData.data.order?.customer?.person

  const handleDecorator = React.useMemo(
    () => decorator(tunnelData),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  return (
    <Container fluid className="white-background">
      <Row noGutters>
        <Col className="main-block">
          <Row>
            <Topbar tunnelData={tunnelData} displayToggleButton={true} history={history} />
            <Header tunnelData={tunnelData} />
            <Col sm={12}>
              <Container fluid>
                <FinalForm.Form
                  initialValues={tunnelDataToFinalForm(person, customer, country, tunnelData)}
                  decorators={[handleDecorator, focusOnErrors]}
                  validate={validateFields}
                  onSubmit={submit}
                  render={({ handleSubmit, values }) => {
                    return (
                      <IdentificationPageForm
                        onSubmit={handleSubmit}
                        values={values}
                        tunnelData={tunnelData}
                        optins={optins}
                        isLoadingOptins={isLoadingOptins}
                      />
                    )
                  }}
                />
              </Container>
            </Col>
            <Col>
              <Container fluid>
                <Footer tunnelData={tunnelData} infosAndClientPageDisplay={true} />
              </Container>
            </Col>
          </Row>
        </Col>

        <Col className="aside-block d-none d-lg-block d-xl-block" lg={4}>
          <Basket tunnelData={tunnelData || null} />
        </Col>
      </Row>

      <TradeInModals
        showTradeInModal={showTradeInModal}
        showIframeModal={showIframeModal}
        showLoaderModal={showLoaderModal}
        loaderStatus={loaderStatus}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        onCloseIframe={handleCloseIframe}
        onLoaderContinue={handleLoaderContinue}
        tunnelData={tunnelData}
      />
    </Container>
  )
}

export default IdentificationPage
